import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import StatusIndicatorBar from '../../components/ui/StatusIndicatorBar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { getConnectionStatus } from '../../services/mercadoLivreOAuthService';
import { productService } from '../../services/productService';

const DashboardOverview = () => {
  const navigate = useNavigate();
  const [mlStatus, setMlStatus] = useState({ connected: false, loading: true });
  const [shopeeStatus, setShopeeStatus] = useState({ connected: false, loading: true });
  const [stats, setStats] = useState({
    totalProducts: 0,
    activeListings: 0,
    readyProducts: 0,
    pendingProducts: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load Mercado Livre status
      const mlResult = getConnectionStatus();
      setMlStatus({
        connected: mlResult?.connected || false,
        user_id: mlResult?.user_id,
        expires_at: mlResult?.expires_at,
        loading: false
      });

      // Load Shopee status (check env vars)
      const shopeeConfigured = !!import.meta.env?.VITE_SHOPEE_PARTNER_ID && 
                               !!import.meta.env?.VITE_SHOPEE_PARTNER_KEY;
      setShopeeStatus({
        connected: shopeeConfigured,
        loading: false
      });

      // Load product statistics
      const { products } = await productService?.getAll();
      const totalProducts = products?.length || 0;
      const readyProducts = products?.filter(p => p?.status === 'ready')?.length || 0;
      const pendingProducts = products?.filter(p => p?.status === 'pending')?.length || 0;
      
      setStats({
        totalProducts,
        activeListings: readyProducts,
        readyProducts,
        pendingProducts
      });

    } catch (err) {
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMLSettings = () => {
    navigate('/mercado-livre-o-auth-connection');
  };

  const handleShopeeSettings = () => {
    // Navigate to Shopee settings (placeholder)
    alert('Configuração Shopee em desenvolvimento');
  };

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard-overview' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <StatusIndicatorBar />
      
      <div className="container mx-auto px-4 py-6 space-y-6">
        <BreadcrumbNavigation items={breadcrumbItems} />

        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Dashboard Overview</h1>
            <p className="text-muted-foreground">Central de controle e monitoramento de integrações</p>
          </div>
          <Button
            variant="outline"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={loadDashboardData}
            disabled={loading}
          >
            Atualizar
          </Button>
        </div>

        {/* API Connection Status Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Mercado Livre Connection Card */}
          <div className="bg-card border border-border rounded-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10">
                  <Icon name="Store" size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Mercado Livre</h3>
                  <p className="caption text-muted-foreground">Integração de marketplace</p>
                </div>
              </div>
              <button
                onClick={handleMLSettings}
                className="w-8 h-8 rounded-lg bg-muted/30 border border-border hover:bg-muted flex items-center justify-center transition-smooth"
                title="Configurações"
              >
                <Icon name="Settings" className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>

            <div className="space-y-3">
              {/* Connection Status */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                <span className="text-sm text-muted-foreground">Status da Conexão</span>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: mlStatus?.connected ? '#22c55e' : '#ef4444'
                    }}
                  />
                  <span 
                    className="text-sm font-semibold"
                    style={{
                      color: mlStatus?.connected ? '#22c55e' : '#ef4444'
                    }}
                  >
                    {mlStatus?.loading ? 'Verificando...' : mlStatus?.connected ? 'Conectado' : 'Desconectado'}
                  </span>
                </div>
              </div>

              {/* User ID */}
              {mlStatus?.connected && mlStatus?.user_id && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <span className="text-sm text-muted-foreground">ID do Usuário</span>
                  <span className="text-sm font-medium text-foreground">{mlStatus?.user_id}</span>
                </div>
              )}

              {/* Token Expiry */}
              {mlStatus?.connected && mlStatus?.expires_at && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <span className="text-sm text-muted-foreground">Token Expira em</span>
                  <span className="text-sm font-medium text-foreground">
                    {new Date(mlStatus?.expires_at)?.toLocaleDateString('pt-BR')}
                  </span>
                </div>
              )}
            </div>

            {!mlStatus?.connected && (
              <Button
                variant="default"
                iconName="Link"
                iconPosition="left"
                onClick={handleMLSettings}
                fullWidth
              >
                Conectar Mercado Livre
              </Button>
            )}
          </div>

          {/* Shopee Connection Card */}
          <div className="bg-card border border-border rounded-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-accent/10">
                  <Icon name="ShoppingBag" size={24} color="var(--color-accent)" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Shopee</h3>
                  <p className="caption text-muted-foreground">Integração de marketplace</p>
                </div>
              </div>
              <button
                onClick={handleShopeeSettings}
                className="w-8 h-8 rounded-lg bg-muted/30 border border-border hover:bg-muted flex items-center justify-center transition-smooth"
                title="Configurações"
              >
                <Icon name="Settings" className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>

            <div className="space-y-3">
              {/* Connection Status */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                <span className="text-sm text-muted-foreground">Status da Conexão</span>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: shopeeStatus?.connected ? '#22c55e' : '#ef4444'
                    }}
                  />
                  <span 
                    className="text-sm font-semibold"
                    style={{
                      color: shopeeStatus?.connected ? '#22c55e' : '#ef4444'
                    }}
                  >
                    {shopeeStatus?.loading ? 'Verificando...' : shopeeStatus?.connected ? 'Configurado' : 'Não Configurado'}
                  </span>
                </div>
              </div>

              {/* Configuration Status */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                <span className="text-sm text-muted-foreground">Credenciais API</span>
                <span className="text-sm font-medium text-foreground">
                  {shopeeStatus?.connected ? 'Configuradas' : 'Não Configuradas'}
                </span>
              </div>
            </div>

            {!shopeeStatus?.connected && (
              <Button
                variant="default"
                iconName="Link"
                iconPosition="left"
                onClick={handleShopeeSettings}
                fullWidth
              >
                Configurar Shopee
              </Button>
            )}
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                <Icon name="Package" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <p className="caption text-muted-foreground">Total de Produtos</p>
                <p className="text-2xl font-bold text-foreground">{stats?.totalProducts}</p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-success/10">
                <Icon name="CheckCircle" size={20} color="#22c55e" />
              </div>
              <div>
                <p className="caption text-muted-foreground">Produtos Prontos</p>
                <p className="text-2xl font-bold text-foreground">{stats?.readyProducts}</p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-warning/10">
                <Icon name="Clock" size={20} color="var(--color-warning)" />
              </div>
              <div>
                <p className="caption text-muted-foreground">Pendentes</p>
                <p className="text-2xl font-bold text-foreground">{stats?.pendingProducts}</p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-accent/10">
                <Icon name="TrendingUp" size={20} color="var(--color-accent)" />
              </div>
              <div>
                <p className="caption text-muted-foreground">Anúncios Ativos</p>
                <p className="text-2xl font-bold text-foreground">{stats?.activeListings}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="font-semibold text-foreground mb-4">Ações Rápidas</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/csv-import-dashboard')}
              className="flex flex-col items-center gap-3 p-4 rounded-lg border border-border bg-background hover:bg-accent hover:border-primary transition-smooth text-center group"
            >
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-smooth">
                <Icon name="Upload" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <p className="font-medium text-foreground">Importar Produtos</p>
                <p className="caption text-muted-foreground">Upload via CSV</p>
              </div>
            </button>

            <button
              onClick={() => navigate('/product-catalog-manager')}
              className="flex flex-col items-center gap-3 p-4 rounded-lg border border-border bg-background hover:bg-accent hover:border-primary transition-smooth text-center group"
            >
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-smooth">
                <Icon name="Package" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <p className="font-medium text-foreground">Gerenciar Catálogo</p>
                <p className="caption text-muted-foreground">Editar produtos</p>
              </div>
            </button>
          </div>
        </div>

        {/* Activity Feed */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="font-semibold text-foreground mb-4">Atividade Recente</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-success/10">
                <Icon name="CheckCircle" size={16} color="#22c55e" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Sistema inicializado</p>
                <p className="caption text-muted-foreground">Dashboard carregado com sucesso</p>
              </div>
              <span className="caption text-muted-foreground">Agora</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;